import React from "react";
import { Link } from "react-router-dom";
import FooterSection from "../components/home/FooterSection";
import Navbar from "../components/Navbar";
import FlyCodeBanner from "../components/home/FlyCodeBanner";

const Org = () => {
  const features = [
    {
      icon: "🚀",
      title: "Early Access to Features",
      description: "Get first access to new tools and features",
      details:
        "Be the first to experience cutting-edge educational tools as they're developed. Our early access program gives your chapter exclusive previews of upcoming features, allowing you to provide feedback and shape the future of EduTools.",
    },
    {
      icon: "🎯",
      title: "Customized Onboarding",
      description: "Personalized setup for your chapter",
      details:
        "Our team will work directly with your chapter leadership to create a customized onboarding experience. We'll help you set up user accounts, configure settings, and provide training sessions tailored to your chapter's specific needs and goals.",
    },
    {
      icon: "💝",
      title: "Annual Philanthropy",
      description: "We donate to your chapter's causes",
      details:
        "EduTools believes in giving back to the community. Each year, we make a donation to your chapter's philanthropy or community service initiatives. This helps support the causes your chapter cares about while strengthening our partnership.",
    },
    {
      icon: "📊",
      title: "Chapter Analytics",
      description: "Track usage and engagement (coming soon)",
      details:
        "Get detailed insights into how your chapter members are using EduTools. Track engagement metrics, popular tools, study patterns, and more. This data helps you understand the impact EduTools has on your chapter's academic success.",
    },
    {
      icon: "🎧",
      title: "Priority Support",
      description: "Direct line to our support team",
      details:
        "Skip the queue with dedicated priority support. Your chapter gets direct access to our support team with faster response times, personalized assistance, and priority resolution for any issues or questions you may have.",
    },
    {
      icon: "🎓",
      title: "EduTools Scholarships",
      description: "Scholarship opportunities for members",
      details:
        "We provide scholarship opportunities exclusively for EduTools chapter members. These scholarships help support your members' educational goals and demonstrate our commitment to student success and academic achievement.",
    },
    {
      icon: "📚",
      title: "Sponsored Study Sessions",
      description: "We cover your chapter's study costs",
      details:
        "EduTools sponsors study sessions and academic events for your chapter. We cover venue costs, refreshments, study materials, and other expenses to help create productive learning environments for your members.",
    },
    {
      icon: "💡",
      title: "Platform Suggestions",
      description: "We take suggestions from EduTools chapters",
      details:
        "Your voice matters in shaping EduTools. We actively collect and implement suggestions from our chapter community. Whether it's new tools, features, or improvements, your feedback directly influences our development roadmap and helps us build the platform you need.",
    },
  ];

  const tools = [
    { name: "Chegg", logo: "📚" },
    { name: "Canva", logo: "🎨" },
    { name: "Quizlet", logo: "🧠" },
    { name: "Grammarly", logo: "✍️" },
    { name: "GPT", logo: "🤖" },
    { name: "Symbolab", logo: "🔢" },
    { name: "Quillbot", logo: "✏️" },
    { name: "CourseHero", logo: "📖" },
    { name: "More always being added", logo: "➕" },
  ];

  const withoutEduTools = [
    {
      emoji: "💸",
      title: "High Individual Costs",
      text: "Each tool costs $20-30/month per student",
    },
    {
      emoji: "🔄",
      title: "Tool Switching",
      text: "Constantly switching between different platforms",
    },
    {
      emoji: "📊",
      title: "No Analytics",
      text: "No insights into chapter usage patterns",
    },
    {
      emoji: "⏰",
      title: "Time Wasted",
      text: "Learning different interfaces for each tool",
    },
    {
      emoji: "🔒",
      title: "Access Issues",
      text: "Students forget passwords and lose access",
    },
    {
      emoji: "📈",
      title: "Growing Costs",
      text: "Prices increase annually with each subscription",
    },
  ];

  const withEduTools = [
    {
      emoji: "💰",
      title: "Massive Savings",
      text: "Save $2,400+ annually per member",
    },
    {
      emoji: "🎯",
      title: "One Platform",
      text: "All tools accessible through single login",
    },
    {
      emoji: "📊",
      title: "Chapter Analytics",
      text: "Track usage and engagement across your chapter",
    },
    {
      emoji: "⚡",
      title: "Instant Access",
      text: "No setup time - start using tools immediately",
    },
    {
      emoji: "🔑",
      title: "Single Sign-On",
      text: "One password for everything",
    },
    {
      emoji: "📉",
      title: "Fixed Pricing",
      text: "No surprise price increases",
    },
    {
      emoji: "🎓",
      title: "Scholarships",
      text: "EduTools provides scholarships to your members",
    },
    {
      emoji: "🤝",
      title: "Community Support",
      text: "Dedicated support for your chapter's needs",
    },
  ];

  return (
    <div className="App">
      <FlyCodeBanner />
      <Navbar />

      {/* Hero Section */}
      <div
        style={{
          background:
            "radial-gradient(98.87% 98.87% at 51.11% 1.13%, #000000 0%, #000000 45.31%, #0124D3 76.56%, #0051FF 86.46%, #83A4FF 100%)",
          padding: "80px 16px 60px",
          marginTop: "80px",
          minHeight: "600px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "0px",
          position: "relative",
          gap: "20px",
        }}
      >
        {/* Attention Badge */}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            padding: "6px 10px",
            gap: "10px",
            background: "rgba(123, 123, 123, 0.16)",
            borderRadius: "30px",
            maxWidth: "fit-content",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: "0",
              borderRadius: "30px",
              padding: "1.5px",
              background:
                "linear-gradient(180deg, #DDEFFF 0%, rgba(212, 226, 255, 0.27) 36%, rgba(212, 226, 255, 0.31) 69%, rgba(212, 226, 255, 0.92) 100%)",
              WebkitMask:
                "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
              WebkitMaskComposite: "xor",
              maskComposite: "exclude",
            }}
          />
          <p
            style={{
              margin: "0",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontStyle: "normal",
              fontWeight: "400",
              fontSize: "12px",
              lineHeight: "100%",
              textAlign: "center",
              color: "#FFFFFF",
              textShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
              whiteSpace: "nowrap",
              position: "relative",
              zIndex: "1",
            }}
          >
            Attention: Board Members & Students
          </p>
        </div>

        {/* Main Headline */}
        <h1
          style={{
            maxWidth: "1100px",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontStyle: "normal",
            fontWeight: "700",
            fontSize: "50px",
            lineHeight: "100%",
            textAlign: "center",
            color: "#FFFFFF",
            margin: "0",
            padding: "0 16px",
          }}
        >
          You'd Be Surprised How
          <br />
          Much This One Simple Solution
          <br />
          Can Improve Your Chapter
        </h1>

        {/* Subtext */}
        <div
          style={{
            maxWidth: "1100px",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontStyle: "normal",
            fontWeight: "500",
            fontSize: "13px",
            lineHeight: "120%",
            textAlign: "center",
            color: "#FFFFFF",
            padding: "0 16px",
          }}
        >
          <p style={{ margin: "0 0 4px 0" }}>
            Bring your chapter a tool that's effortless to adopt but powerful in
            its impact across every part of your organization.
          </p>
          <p style={{ margin: "0" }}>
            Learn how the chapters who've taken this initiative have watched
            their organizations absolutely thrive.
          </p>
        </div>
      </div>

      {/* Tools Comparison Table */}
      <div
        style={{
          padding: "60px 16px",
          background: "white",
        }}
      >
        <div
          className="container"
          style={{ maxWidth: "1000px", margin: "0 auto" }}
        >
          <h2
            style={{
              fontSize: "2rem",
              fontWeight: "600",
              textAlign: "center",
              marginBottom: "2rem",
              color: "#1a202c",
              padding: "0 16px",
            }}
          >
            Replace Multiple Paid Tools
          </h2>

          {/* Comparison Table */}
          <div
            style={{
              background: "white",
              borderRadius: "20px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              overflow: "hidden",
              border: "1px solid #e2e8f0",
              margin: "0 16px",
            }}
          >
            {/* Table Header */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                background: "#f8fafc",
                borderBottom: "2px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  padding: "1rem",
                  textAlign: "center",
                  fontSize: "0.9rem",
                  fontWeight: "600",
                  color: "#4a5568",
                  borderRight: "1px solid #e2e8f0",
                  lineHeight: "1.3",
                }}
              >
                Individual Subscriptions
              </div>
              <div
                style={{
                  padding: "1rem",
                  textAlign: "center",
                  fontSize: "0.9rem",
                  fontWeight: "600",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  position: "relative",
                  lineHeight: "1.3",
                }}
              >
                EduTools All-in-One
              </div>
            </div>

            {/* Tools Comparison Rows */}
            {tools.map((tool, index) => (
              <div
                key={index}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  borderBottom:
                    index < tools.length - 1 ? "1px solid #e2e8f0" : "none",
                }}
              >
                {/* Individual Subscription Side */}
                <div
                  style={{
                    padding: "0.75rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    borderRight: "1px solid #e2e8f0",
                    background: "#fefefe",
                    minHeight: "60px",
                  }}
                >
                  {tool.name !== "More always being added" && (
                    <>
                      <div
                        style={{
                          fontSize: "1.2rem",
                          opacity: "0.6",
                          flexShrink: "0",
                        }}
                      >
                        {tool.logo}
                      </div>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          fontWeight: "500",
                          color: "#4a5568",
                          lineHeight: "1.2",
                          flex: "1",
                          minWidth: "0",
                        }}
                      >
                        {tool.name}
                      </div>
                    </>
                  )}
                </div>

                {/* EduTools Side */}
                <div
                  style={{
                    padding: "0.75rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    background:
                      "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)",
                    position: "relative",
                    minHeight: "60px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "1.2rem",
                      flexShrink: "0",
                    }}
                  >
                    {tool.logo}
                  </div>
                  <div
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: "600",
                      color: "#667eea",
                      lineHeight: "1.2",
                      flex: "1",
                      minWidth: "0",
                    }}
                  >
                    {tool.name}
                  </div>
                </div>
              </div>
            ))}

            {/* Price Comparison Footer */}
            <div
              style={{
                background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                padding: "1.5rem 1rem",
                borderTop: "2px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  textAlign: "center",
                  gap: "1rem",
                }}
              >
                {/* Individual Cost */}
                <div
                  style={{
                    flex: "1",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "1.2rem",
                      fontWeight: "700",
                      color: "#e53e3e",
                      textDecoration: "line-through",
                    }}
                  >
                    $2,400+
                  </div>
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#666",
                      marginTop: "0.25rem",
                    }}
                  >
                    Annual cost
                  </div>
                </div>

                {/* VS */}
                <div
                  style={{
                    fontSize: "0.9rem",
                    fontWeight: "600",
                    color: "#667eea",
                    background: "white",
                    padding: "0.3rem 0.6rem",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    flexShrink: "0",
                  }}
                >
                  VS
                </div>

                {/* EduTools Cost */}
                <div
                  style={{
                    flex: "1",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      fontSize: "1.2rem",
                      fontWeight: "700",
                      color: "#38a169",
                    }}
                  >
                    $270
                  </div>
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#666",
                      marginTop: "0.25rem",
                    }}
                  >
                    With EduTools
                  </div>
                </div>
              </div>

              {/* Savings Highlight */}
              <div
                style={{
                  textAlign: "center",
                  marginTop: "1rem",
                  padding: "0.8rem",
                  background:
                    "linear-gradient(135deg, #38a169 0%, #2d7a3d 100%)",
                  borderRadius: "10px",
                  color: "white",
                }}
              >
                <div
                  style={{
                    fontSize: "1rem",
                    fontWeight: "600",
                    marginBottom: "0.3rem",
                  }}
                >
                  🎉 Save $2,130+ per member annually!
                </div>
                <div
                  style={{
                    fontSize: "0.8rem",
                    opacity: "0.9",
                    lineHeight: "1.3",
                  }}
                >
                  Plus get access to new tools as they're added
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feature List Section */}
      <div
        style={{
          padding: "60px 16px",
          background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
        }}
      >
        <div
          className="container"
          style={{ maxWidth: "1000px", margin: "0 auto" }}
        >
          <h2
            style={{
              fontSize: "2rem",
              fontWeight: "600",
              textAlign: "center",
              marginBottom: "2rem",
              color: "#1a202c",
              padding: "0 16px",
            }}
          >
            Everything in Pro, plus...
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "1.5rem",
              marginTop: "2rem",
              padding: "0 16px",
            }}
          >
            {features.map((feature, index) => (
              <div
                key={index}
                style={{
                  background: "white",
                  padding: "1.5rem",
                  borderRadius: "12px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  border: "1px solid #f1f5f9",
                }}
              >
                <div
                  style={{
                    fontSize: "2rem",
                    marginBottom: "1rem",
                  }}
                >
                  {feature.icon}
                </div>
                <h3
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: "600",
                    marginBottom: "0.5rem",
                    color: "#1e293b",
                  }}
                >
                  {feature.title}
                </h3>
                <p
                  style={{
                    fontSize: "0.9rem",
                    color: "#64748b",
                    lineHeight: "1.5",
                    margin: "0",
                  }}
                >
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Comparison Section */}
      <div
        style={{
          padding: "60px 16px",
          background: "white",
        }}
      >
        <div
          className="container"
          style={{ maxWidth: "1200px", margin: "0 auto" }}
        >
          <h2
            style={{
              fontSize: "2rem",
              fontWeight: "600",
              textAlign: "center",
              marginBottom: "3rem",
              color: "#1a202c",
              padding: "0 16px",
            }}
          >
            The Difference is Clear
          </h2>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "2rem",
            }}
          >
            {/* Without EduTools */}
            <div>
              <h3
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "600",
                  marginBottom: "1.5rem",
                  color: "#e53e3e",
                  textAlign: "center",
                }}
              >
                Without EduTools
              </h3>
              <div
                style={{
                  display: "grid",
                  gap: "0.75rem",
                }}
              >
                {withoutEduTools.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      background: "#fed7d7",
                      padding: "1.25rem",
                      borderRadius: "12px",
                      border: "1px solid #feb2b2",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "1.25rem",
                        marginBottom: "0.5rem",
                      }}
                    >
                      {item.emoji}
                    </div>
                    <h4
                      style={{
                        fontSize: "1rem",
                        fontWeight: "600",
                        marginBottom: "0.5rem",
                        color: "#742a2a",
                      }}
                    >
                      {item.title}
                    </h4>
                    <p
                      style={{
                        fontSize: "0.9rem",
                        color: "#742a2a",
                        lineHeight: "1.4",
                      }}
                    >
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* With EduTools */}
            <div>
              <h3
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "600",
                  marginBottom: "1.5rem",
                  color: "#38a169",
                  textAlign: "center",
                }}
              >
                With EduTools
              </h3>
              <div
                style={{
                  display: "grid",
                  gap: "0.75rem",
                }}
              >
                {withEduTools.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      background: "#c6f6d5",
                      padding: "1.25rem",
                      borderRadius: "12px",
                      border: "1px solid #9ae6b4",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "1.25rem",
                        marginBottom: "0.5rem",
                      }}
                    >
                      {item.emoji}
                    </div>
                    <h4
                      style={{
                        fontSize: "1rem",
                        fontWeight: "600",
                        marginBottom: "0.5rem",
                        color: "#22543d",
                      }}
                    >
                      {item.title}
                    </h4>
                    <p
                      style={{
                        fontSize: "0.9rem",
                        color: "#22543d",
                        lineHeight: "1.4",
                      }}
                    >
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quote Banner */}
      <div
        style={{
          padding: "60px 16px",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
        }}
      >
        <div
          className="container"
          style={{ maxWidth: "800px", margin: "0 auto", textAlign: "center" }}
        >
          <blockquote
            style={{
              fontSize: "1.4rem",
              fontWeight: "500",
              fontStyle: "italic",
              marginBottom: "1.5rem",
              lineHeight: "1.4",
              padding: "0 16px",
            }}
          >
            "EduTools has transformed how our chapter approaches learning. The
            savings alone justify the investment, but the community impact is
            priceless."
          </blockquote>
          <div
            style={{
              fontSize: "1rem",
              opacity: "0.9",
              lineHeight: "1.6",
              padding: "0 16px",
              marginBottom: "2rem",
            }}
          >
            Join thousands of students who are already saving money and
            improving their academic experience with EduTools.
          </div>
          <div
            style={{
              marginTop: "1rem",
            }}
          >
            <Link
              to="/pricing"
              style={{
                background: "white",
                color: "#667eea",
                padding: "14px 28px",
                borderRadius: "12px",
                textDecoration: "none",
                fontWeight: "600",
                fontSize: "1rem",
                display: "inline-block",
                transition: "all 0.3s ease",
              }}
            >
              Get Started Today
            </Link>
          </div>
        </div>
      </div>

      <FooterSection />
    </div>
  );
};

export default Org;
